import * as Sentry from "@sentry/react";
import {AnyAction} from "redux";

/**
 * Initialize the Sentry browser SDK for the web app. Call this once at app
 * boot before the React tree mounts. Configures:
 *
 * - DSN + environment + release tags from VITE_ENVIRONMENT
 * - `tracesSampleRate` 0 — performance monitoring is off entirely; we don't use
 *   the data and the org's shared perf quota was exhausted, which made Sentry
 *   429 the ingest endpoint. Errors are unaffected. See the inline note below.
 * - `ignoreErrors` allowlist for known non-actionable patterns (auth-not-yet,
 *   browser extension noise, in-app webview bridge errors, transient network
 *   failures, etc.)
 * - `beforeSend` hook that drops two specific noise patterns: raw Response
 *   objects thrown by the generated client (re-captured properly in addError),
 *   and Statsig SDK SecurityErrors from cross-origin webview contexts.
 */
export function initSentry(): void {
    /*
     * Skip Sentry on localhost / non-deployed environments. Every event
     * gets POSTed to `o4505830066159616.ingest.sentry.io` and a) most of
     * those POSTs are blocked at the network layer by ad blockers,
     * b) the failures litter the dev console with "Fetch failed loading
     * POST" entries, c) the events are not actionable (dev clicks, hot
     * reloads, test data). Sentry's own fetch instrumentation masks the
     * outgoing URLs to `<URL>` to avoid breadcrumb loops, which is what
     * was making the failures opaque in the console.
     *
     * Production deployments still report normally — they ship with
     * `VITE_ENVIRONMENT=production`.
     */
    const isLocalDev = typeof window !== "undefined" &&
        (window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1");
    if (isLocalDev) return;

    Sentry.init({
        dsn: "https://858ba88ada6fe724d2dac07e2a677073@o4505830066159616.ingest.sentry.io/4505830068256768",
        tracePropagationTargets: [
            "localhost",
            /^https:\/\/devourgo\.io/,
            /*
             * Match any subdomain of web.devourgo.io. The previous pattern
             * `\/\/+\.web\.devourgo\.io` was a broken regex: `\/\/+` means
             * "two or more slashes" and `.web` started with a literal dot,
             * so it never matched a real URL (real URLs have exactly `//`
             * followed by a host, not a dot). The corrected pattern uses
             * `[^/]+` to capture the subdomain segment.
             */
            /^https:\/\/[^/]+\.web\.devourgo\.io/,
        ],
        environment: import.meta.env.VITE_ENVIRONMENT,
        /*
         * The DEPLOY TAG, not the environment name.
         *
         * `release` and `environment` were both VITE_ENVIRONMENT, so every web
         * event since forever is tagged `release: "production"` and the release
         * dimension does nothing. Sentry's regression detection, release health
         * and "resolved in next release" all key off this field and silently
         * no-op; "did this start with tag X?" is unanswerable for web.
         *
         * VITE_APP_VERSION is the deploy tag. It was NOT reaching the bundle:
         * the Dockerfile published the tag as `APP_VERSION`, and Vite only
         * exposes VITE_-prefixed variables to import.meta.env — so the one
         * other consumer, widgets/PageFooter.tsx, had been rendering "DEV" as
         * the production build label. `ENV VITE_APP_VERSION=$APP_VERSION` in
         * the Dockerfile closes that; no new build arg or Cloud Build
         * substitution was needed. Falls back to the environment name rather
         * than shipping `undefined`, which Sentry treats as no release at all.
         */
        release: (import.meta.env.VITE_APP_VERSION as string | undefined) || import.meta.env.VITE_ENVIRONMENT,
        integrations: [
            /*
             * Browser profiling removed - causes document policy violations and rate limiting.
             * Sentry Session Replay removed - we use a different system for session replays.
             * Browser tracing removed - see tracesSampleRate below. The router integration
             * exists only to open a transaction per navigation; with performance monitoring
             * off it produces envelopes that are rejected before they're stored.
             */
        ],
        normalizeDepth: 10,
        /*
         * Performance Monitoring — OFF. We don't use the data, and collecting it
         * was actively costing us: the org's performance quota is shared across
         * every project on this DSN, and once exhausted Sentry answers 429 to the
         * ingest endpoint. That is the `429 (Too Many Requests)` on
         * `ingest.sentry.io/api/4505830068256768/envelope/` visible in the
         * production console on /play.
         *
         * Measured (org stats_v2, 7d to 2026-08-06): every transaction, span and
         * profile envelope org-wide was rejected with `transaction_usage_exceeded`
         * — 1,830,611 spans and 56,706 transactions dropped, zero accepted. The
         * backend (see backend/src/utils/sentry.ts) was 88% of that volume; this
         * app contributed 221,427 spans.
         *
         * Error reporting does NOT depend on tracesSampleRate and was never
         * rate-limited, so exceptions continue to report normally from prod and
         * from every mirror.
         *
         * (Prior note, still true: non-prod deployed environments share this DSN,
         * so a non-zero non-prod rate also flooded the project with perf issues
         * sourced from dev builds — DEVOUR-WEB-1D5 et al, tracing to Vite
         * `.vite/deps` assets on localhost:8085 rather than real user traffic.)
         */
        tracesSampleRate: 0,
        ignoreErrors: [
            "Non-Error promise rejection captured",
            "Non-Error exception captured",
            // Authentication state — expected for unauthenticated visitors
            "Missing authentication token",
            // Browser extension errors - not our code
            "Invalid call to runtime.sendMessage()",
            "runtime.sendMessage",
            // In-app browser WebView bridge errors - not actionable, third-party environment
            "Java object is gone", // Android Facebook/Instagram in-app browser
            "Error invoking postMessage", // Android WebView bridge recycled
            "webkit.messageHandlers", // iOS non-WKWebView browsers (polyfilled but may still log)
            // Video playback race condition - harmless, user sees nothing
            "The play() request was interrupted",
            // WalletConnect — user's wallet session expired, not a bug
            "Proposal expired",
            // Browser geolocation denied/timeout — user action, not a bug
            "GeolocationPositionError",
            /*
             * Vite dynamic import failures after deploy — chunkLoadRetry.ts force-reloads
             * fresh index.html; the pre-reload capture is self-recovering noise. Cover
             * every browser's wording for the same root cause (iOS WKWebView emits
             * "Importing a module script failed"; DEVOUR-WEB-2XQ).
             */
            "Failed to fetch dynamically imported module",
            "Importing a module script failed",
            "error loading dynamically imported module",
            "Unable to preload CSS",
            /*
             * Vanity-slug resolver miss — expected negative, not a bug. Every
             * unknown single-segment path (dead/old brand or drop links, typos,
             * bots) hits GET /vanity/:slug, which returns "No host found at this
             * slug." VanitySlugResolver already handles this gracefully by
             * redirecting home (which then lands an authed user on /play — the
             * reason this mis-attributed to the /play transaction). Nothing to
             * fix in the flow; tracking it as an error just floods the project
             * (DEVOUR-WEB-3NF, 249 users). Same category as the allowlist above.
             */
            "No host found at this slug",
            // Browser extension schema.org parsing — not our code
            "extractSchemaValuesFromSchemaOrg",
            "extractFilteredSchemaValuesFromJSONLD",
            /*
             * Sentry's own SDK failing to read its session id (flaky session/replay
             * tracking, esp. in the Overwolf bundle). Not our code; benign for 14+ mo.
             */
            "Cannot read properties of undefined (reading 'getSessionId')",
        ],
        beforeSend(event) {
            const values = event.exception?.values;
            if (!values?.length) return event;
            for (const v of values) {
                /*
                 * Legacy shape only. Up to openapi-generator 4.3.0 the client
                 * threw raw Response objects on non-2xx, which stringified as
                 * "[object Response]" with no useful title. 7.14.0 (2026-08-16)
                 * throws a ResponseError wrapper instead, so this no longer
                 * matches anything the current client produces — kept only for
                 * cached bundles still running the old client.
                 *
                 * NOTE the second half of the original rationale ("the
                 * meaningful 5xx case is already re-captured as a typed ApiError
                 * in addError") stopped being true when that upgrade landed:
                 * the re-capture was gated on a httpStatus the broken parse
                 * never set, so dropping here left 5xx with NO representation at
                 * all. Do not add a drop like this without confirming the
                 * fallback capture actually fires.
                 */
                if (v.value === "[object Response]") return null;
                /*
                 * Statsig SDK reads window.parent.origin during init; cross-origin webview
                 * contexts (FB/Instagram IAB, some mobile browsers) throw SecurityError before
                 * the SDK guards. Page degrades to default treatments — not actionable here,
                 * tracked separately for upstream/ErrorBoundary follow-up.
                 * Guard `typeof v.value === "string"` — if a non-standard error
                 * ever delivers a non-string value, calling .includes() would throw
                 * synchronously inside beforeSend and crash the Sentry pipeline,
                 * silently dropping ALL events for that page session.
                 */
                if (v.type === "SecurityError" && typeof v.value === "string" && v.value.includes("cross-origin frame")) return null;
            }
            return event;
        },
    });

    /*
     * Drain the early-error buffer captured by index.html before Sentry
     * loaded. The deferred dynamic import in main.tsx introduces a
     * ~100-500ms gap between page load and Sentry's global handlers
     * being installed; index.html's inline window.__earlyErrors buffer
     * preserves anything thrown in that window so it can be replayed
     * here through the normal capture pipeline.
     *
     * Each buffered entry is captured as either an exception or a
     * generic event depending on the event kind. The buffer is cleared
     * after drain so a hot-reload or repeat init doesn't double-report.
     */
    const buffered = (window as any).__earlyErrors as Array<{
        kind: "error" | "unhandledrejection";
        error?: unknown;
        reason?: unknown;
        message?: string;
        filename?: string;
        lineno?: number;
        colno?: number;
        ts: number;
    }> | undefined;
    if (Array.isArray(buffered) && buffered.length) {
        for (const entry of buffered) {
            try {
                const payload = entry.kind === "unhandledrejection" ? entry.reason : entry.error ?? entry.message;
                Sentry.captureException(payload, {
                    tags: { earlyErrorBuffer: "true", earlyErrorKind: entry.kind },
                    extra: {
                        bufferedAtMs: entry.ts,
                        filename: entry.filename,
                        lineno: entry.lineno,
                        colno: entry.colno,
                    },
                });
            } catch {
                // Sentry capture failure shouldn't block remaining drains
            }
        }
        (window as any).__earlyErrors = [];
    }
}

/**
 * Returns the Sentry Redux store enhancer. Wires Redux state and action
 * dispatches into Sentry's scope so breadcrumbs and crash reports include
 * the action stream that preceded an error.
 *
 * SECURITY: `createReduxEnhancer` defaults to attaching the FULL Redux state to
 * every captured event and recording every action payload as a breadcrumb.
 * `authStore.fullToken` holds the live bearer/session token (`Token.token`).
 * Multiple actions carry it: LOGIN's payload IS the token object, and
 * redux-persist's `persist/REHYDRATE` (fired on every boot with a persisted
 * session) carries the auth slice `{fullToken}` as its payload. Without the
 * transformers below, any error ships the raw session token to Sentry — a
 * session-hijack / account-takeover risk for anyone with Sentry access. We redact
 * it from both the attached state and every action payload, keyed on payload SHAPE
 * (any `fullToken`) rather than a fixed action-type allowlist, so REHYDRATE and any
 * future token-carrying action are covered too. Action-type literals mirror
 * `AuthAction` (LOGIN/LOGOUT), kept inline to avoid coupling this util to redux.
 */
export function getSentryReduxEnhancer() {
    return Sentry.createReduxEnhancer({
        actionTransformer: (action: AnyAction): AnyAction | null => {
            if (!action) {
                return action;
            }
            // LOGIN's payload IS the raw Token object; LOGOUT may echo it. Drop it whole.
            if (action.type === "LOGIN" || action.type === "LOGOUT") {
                return {...action, payload: undefined};
            }
            /*
             * Any action whose payload wraps the token (e.g. persist/REHYDRATE key "auth"
             * → {fullToken}) — redact just that field, keep the rest of the breadcrumb.
             */
            if (action.payload && typeof action.payload === "object" && "fullToken" in action.payload) {
                return {...action, payload: {...action.payload, fullToken: "[REDACTED]"}};
            }
            return action;
        },
        stateTransformer: (state: any) => {
            if (!state?.authStore?.fullToken) {
                return state;
            }
            return {
                ...state,
                authStore: {...state.authStore, fullToken: "[REDACTED]"},
            };
        },
    });
}
